"use client"

import { useEffect, useState, useTransition, useMemo, useCallback } from "react"
import { Box, Text, Group, Badge, Tabs, Table, ActionIcon, Tooltip, Button, Loader, Center, SegmentedControl, Pagination } from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { TbX, TbPencil, TbExternalLink, TbTargetArrow } from "react-icons/tb"
import { SetTPSLModal } from "./SetTPSLModal"

const REGISTRY_URL = process.env.NEXT_PUBLIC_REGISTRY_URL ?? "https://registry.forestsync.com"
import { getUserPositionsGlobalAction, getUserTradeHistoryGlobalAction, type UserPosition, type UserTradeHistoryRow } from "@/actions/trading"
import type { UserOpenOrder } from "@/lib/queries"

/** Primary-market token positions have no originating order — TP isn't offered for these. */
function isExchangeBought(pos: UserPosition) {
  return !pos.refId.startsWith("pm-")
}

function ticker(pos: { projectName: string; creditType: string; vintage: number }) {
  return `${pos.projectName} · ${pos.creditType} ${pos.vintage}`
}

function fmtEur(n: number) {
  return `€${n.toFixed(2)}`
}

interface TradeWatchPanelProps {
  openOrders: UserOpenOrder[]
  marketPrices: Record<string, number>
  onCancelOrder: (id: bigint) => void
  onModifyOrder: (order: UserOpenOrder) => void
  onPlaceOrder: () => void
}

export function TradeWatchPanel({ openOrders, marketPrices, onCancelOrder, onModifyOrder, onPlaceOrder }: TradeWatchPanelProps) {
  const t = useTranslations("trading")
  const [positions, setPositions] = useState<UserPosition[]>([])
  const [history, setHistory] = useState<UserTradeHistoryRow[]>([])
  const [posLoading, startPos] = useTransition()
  const [histLoading, startHist] = useTransition()
  const [aggregated, setAggregated] = useState(true)
  const [posPage, setPosPage] = useState(1)
  const [ordersPage, setOrdersPage] = useState(1)
  const [histPage, setHistPage] = useState(1)
  const [tpModalPosition, setTpModalPosition] = useState<UserPosition | null>(null)

  const PAGE_SIZE = 20

  const refreshPositions = useCallback(() => {
    startPos(async () => {
      const rows = await getUserPositionsGlobalAction()
      setPositions(rows)
    })
  }, [])

  useEffect(() => {
    refreshPositions()
  }, [refreshPositions])

  function loadHistory() {
    startHist(async () => {
      const rows = await getUserTradeHistoryGlobalAction()
      setHistory(rows)
    })
  }

  // Aggregate positions by project + methodology, spanning all vintages
  const aggPositions = useMemo(() => Object.values(
    positions.reduce<Record<string, {
      key: string; projectName: string; creditType: string; vintages: Set<number>; projectRefId: string
      totalVol: number; totalValue: number; count: number; hasPrice: boolean
    }>>((acc, p) => {
      const key = `${p.creditType}|${p.projectRefId}`
      if (!acc[key]) {
        acc[key] = { key, projectName: p.projectName, creditType: p.creditType, vintages: new Set(), projectRefId: p.projectRefId, totalVol: 0, totalValue: 0, count: 0, hasPrice: false }
      }
      acc[key].vintages.add(p.vintage)
      acc[key].totalVol += p.filledVolume
      if (p.price !== null) {
        acc[key].totalValue += p.price * p.filledVolume
        acc[key].hasPrice = true
      }
      acc[key].count += 1
      return acc
    }, {})
  ), [positions])

  const canAggregate = aggPositions.length < positions.length

  const pagedAgg = useMemo(() => aggPositions.slice((posPage - 1) * PAGE_SIZE, posPage * PAGE_SIZE), [aggPositions, posPage])
  const pagedPos = useMemo(() => positions.slice((posPage - 1) * PAGE_SIZE, posPage * PAGE_SIZE), [positions, posPage])
  const pagedOrders = useMemo(() => openOrders.slice((ordersPage - 1) * PAGE_SIZE, ordersPage * PAGE_SIZE), [openOrders, ordersPage])
  const pagedHistory = useMemo(() => history.slice((histPage - 1) * PAGE_SIZE, histPage * PAGE_SIZE), [history, histPage])

  return (
    <Box style={{ height: "100%", display: "flex", flexDirection: "column", borderTop: "1px solid var(--mantine-color-default-border)" }}>
      <Tabs defaultValue="positions" keepMounted={false} h="100%" display="flex" style={{ flexDirection: "column" }}>
        <Tabs.List px={8} style={{ flexShrink: 0 }}>
          <Tabs.Tab value="positions">{t("positionsTab")}</Tabs.Tab>
          <Tabs.Tab value="orders">{t("openOrdersTab")}</Tabs.Tab>
          <Tabs.Tab value="history" onClick={loadHistory}>{t("tradeHistoryTab")}</Tabs.Tab>
          <Box style={{ flex: 1 }} />
          <Button size="xs" variant="light" color="teal" my={4} onClick={onPlaceOrder}>
            {t("placeOrder")}
          </Button>
        </Tabs.List>

        {/* Positions — all markets */}
        <Tabs.Panel value="positions" flex={1} style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Group px={8} py={4} gap={8} style={{ borderBottom: "1px solid var(--mantine-color-default-border)", flexShrink: 0 }}>
            <Tooltip label={t("noAggregationAvailable")} disabled={canAggregate} withArrow>
              <SegmentedControl
                size="xs"
                value={aggregated ? "agg" : "nonagg"}
                onChange={(v) => { setAggregated(v === "agg"); setPosPage(1) }}
                data={[
                  { label: t("aggregated"), value: "agg" },
                  { label: t("nonAggregated"), value: "nonagg" },
                ]}
                disabled={!canAggregate}
              />
            </Tooltip>
          </Group>
          <Box style={{ flex: 1, overflow: "auto" }}>
            {posLoading ? (
              <Center p="md"><Loader size="sm" /></Center>
            ) : positions.length === 0 ? (
              <Text size="xs" c="dimmed" p="xs">{t("noPositions")}</Text>
            ) : aggregated ? (
              <Table withRowBorders={false} verticalSpacing={2} fz="xs" stickyHeader striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th><Text size="xs" c="dimmed">{t("symbol")}</Text></Table.Th>
                    <Table.Th ta="right"><Text size="xs" c="dimmed">{t("lots")}</Text></Table.Th>
                    <Table.Th ta="right"><Text size="xs" c="dimmed">{t("direction")}</Text></Table.Th>
                    <Table.Th ta="right"><Text size="xs" c="dimmed">{t("summaryAvgPrice")}</Text></Table.Th>
                    <Table.Th ta="right"><Text size="xs" c="dimmed">{t("last")}</Text></Table.Th>
                    <Table.Th ta="right"><Text size="xs" c="dimmed">{t("pips")}</Text></Table.Th>
                    <Table.Th ta="right"><Text size="xs" c="dimmed">{t("grossEur")}</Text></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pagedAgg.map((row) => {
                    const avgEntry = row.hasPrice && row.totalVol > 0 ? row.totalValue / row.totalVol : null
                    const isMultiVintage = row.vintages.size > 1
                    const singleVintage = row.vintages.size === 1 ? Array.from(row.vintages)[0] : null
                    const lastPrice = singleVintage != null ? marketPrices[`${row.creditType}|${singleVintage}|${row.projectRefId}`] ?? null : null
                    const pips = lastPrice != null && avgEntry != null ? lastPrice - avgEntry : null
                    const gross = pips != null ? pips * row.totalVol : null
                    const showTotalValue = gross == null && isMultiVintage && row.hasPrice
                    const symbol = singleVintage != null ? `${row.projectName} · ${row.creditType} ${singleVintage}` : `${row.projectName} · ${row.creditType}`
                    return (
                      <Table.Tr key={row.key}>
                        <Table.Td><Text size="xs" ff="monospace">{symbol}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs">{row.totalVol.toFixed(0)}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs" fw={700} c="green">{t("buy")}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs" ff="monospace">{avgEntry != null ? fmtEur(avgEntry) : "N/A"}</Text></Table.Td>
                        <Table.Td ta="right">
                          <Text size="xs" ff="monospace">{lastPrice != null ? fmtEur(lastPrice) : "N/A"}</Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="xs" ff="monospace" c={pips == null ? "dimmed" : pips >= 0 ? "green" : "red"}>
                            {pips != null ? (pips >= 0 ? "+" : "") + pips.toFixed(2) : "N/A"}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="xs" ff="monospace" c={gross != null ? (gross >= 0 ? "green" : "red") : "dimmed"}>
                            {gross != null
                              ? (gross >= 0 ? "+" : "") + fmtEur(Math.abs(gross))
                              : showTotalValue
                                ? fmtEur(row.totalValue)
                                : "N/A"}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            ) : (
              <Table withRowBorders={false} verticalSpacing={2} fz="xs" stickyHeader striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th><Text size="xs" c="dimmed">{t("symbol")}</Text></Table.Th>
                    <Table.Th ta="right"><Text size="xs" c="dimmed">{t("lots")}</Text></Table.Th>
                    <Table.Th ta="right"><Text size="xs" c="dimmed">{t("direction")}</Text></Table.Th>
                    <Table.Th ta="right"><Text size="xs" c="dimmed">{t("entry")}</Text></Table.Th>
                    <Table.Th ta="right"><Text size="xs" c="dimmed">{t("last")}</Text></Table.Th>
                    <Table.Th ta="right"><Text size="xs" c="dimmed">{t("pips")}</Text></Table.Th>
                    <Table.Th ta="right"><Text size="xs" c="dimmed">{t("grossEur")}</Text></Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pagedPos.map((pos) => {
                    const key = `${pos.creditType}|${pos.vintage}|${pos.projectRefId}`
                    const lastPrice = marketPrices[key] ?? null
                    const pips = lastPrice != null && pos.price != null ? lastPrice - pos.price : null
                    const gross = pips != null ? pips * pos.filledVolume : null
                    return (
                      <Table.Tr key={pos.refId}>
                        <Table.Td><Text size="xs" ff="monospace">{ticker(pos)}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs">{pos.filledVolume.toFixed(0)}</Text></Table.Td>
                        <Table.Td ta="right"><Text size="xs" fw={700} c="green">{t("buy")}</Text></Table.Td>
                        <Table.Td ta="right">
                          <Text size="xs" ff="monospace">{pos.price != null ? fmtEur(pos.price) : "N/A"}</Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="xs" ff="monospace">{lastPrice != null ? fmtEur(lastPrice) : "N/A"}</Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="xs" ff="monospace" c={pips == null ? "dimmed" : pips >= 0 ? "green" : "red"}>
                            {pips != null ? (pips >= 0 ? "+" : "") + pips.toFixed(2) : "N/A"}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="xs" ff="monospace" c={gross == null ? "dimmed" : gross >= 0 ? "green" : "red"}>
                            {gross != null ? (gross >= 0 ? "+" : "") + fmtEur(Math.abs(gross)) : "N/A"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {isExchangeBought(pos) && (
                            <Tooltip label={pos.tpOrder ? t("tpsl.tpActive", { vol: pos.tpOrder.volume, price: pos.tpOrder.price.toFixed(2) }) : t("setTp")}>
                              <ActionIcon
                                size="xs"
                                color="teal"
                                variant={pos.tpOrder ? "filled" : "light"}
                                onClick={() => setTpModalPosition(pos)}
                              >
                                <TbTargetArrow size={11} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            )}
            {(aggregated ? aggPositions.length : positions.length) > PAGE_SIZE && (
              <Group justify="center" py="xs">
                <Pagination
                  size="xs"
                  total={Math.ceil((aggregated ? aggPositions.length : positions.length) / PAGE_SIZE)}
                  value={posPage}
                  onChange={setPosPage}
                />
              </Group>
            )}
          </Box>
        </Tabs.Panel>

        {/* Orders — all markets */}
        <Tabs.Panel value="orders" flex={1} style={{ overflow: "auto" }}>
          {openOrders.length === 0 ? (
            <Text size="xs" c="dimmed" p="xs">{t("noOpenOrders")}</Text>
          ) : (
            <>
            <Table withRowBorders={false} verticalSpacing={2} fz="xs" stickyHeader striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th><Text size="xs" c="dimmed">{t("symbol")}</Text></Table.Th>
                  <Table.Th ta="right"><Text size="xs" c="dimmed">{t("lots")}</Text></Table.Th>
                  <Table.Th><Text size="xs" c="dimmed">{t("direction")}</Text></Table.Th>
                  <Table.Th><Text size="xs" c="dimmed">{t("orderType")}</Text></Table.Th>
                  <Table.Th ta="right"><Text size="xs" c="dimmed">{t("price")}</Text></Table.Th>
                  <Table.Th ta="right"><Text size="xs" c="dimmed">{t("filled")}</Text></Table.Th>
                  <Table.Th><Text size="xs" c="dimmed">{t("orderStatus")}</Text></Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pagedOrders.map((order) => {
                  const sym = `${order.projectName} · ${order.creditType} ${order.vintage}`
                  const typeLabel = order.orderType === 1 ? t("limit") : order.orderType === 3 ? t("stop") : t("market")
                  const statusColor = order.status === 2 ? "orange" : "blue"
                  const statusLabel = order.status === 2 ? t("partial") : t("statusOpen")
                  return (
                    <Table.Tr key={order.refId}>
                      <Table.Td><Text size="xs" ff="monospace">{sym}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="xs">{order.volume}</Text></Table.Td>
                      <Table.Td>
                        <Text size="xs" fw={700} c={order.side === 1 ? "green" : "red"}>
                          {order.side === 1 ? t("buy") : t("sell")}
                        </Text>
                      </Table.Td>
                      <Table.Td><Text size="xs">{typeLabel}</Text></Table.Td>
                      <Table.Td ta="right">
                        <Text size="xs" ff="monospace">{order.price != null ? fmtEur(order.price) : "MKT"}</Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="xs">{order.filledVolume}/{order.volume}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="xs" color={statusColor} variant="light">{statusLabel}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          <Tooltip label={t("modifyOrder")}>
                            <ActionIcon size="xs" color="blue" variant="light" onClick={() => onModifyOrder(order)}>
                              <TbPencil size={11} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t("cancelOrder")}>
                            <ActionIcon size="xs" color="red" variant="light" onClick={() => onCancelOrder(order.id)}>
                              <TbX size={11} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
            {openOrders.length > PAGE_SIZE && (
              <Group justify="center" py="xs">
                <Pagination size="xs" total={Math.ceil(openOrders.length / PAGE_SIZE)} value={ordersPage} onChange={setOrdersPage} />
              </Group>
            )}
            </>
          )}
        </Tabs.Panel>

        {/* History — all markets */}
        <Tabs.Panel value="history" flex={1} style={{ overflow: "auto" }}>
          {histLoading ? (
            <Center p="md"><Loader size="sm" /></Center>
          ) : history.length === 0 ? (
            <Text size="xs" c="dimmed" p="xs">{t("noTradeHistory")}</Text>
          ) : (
            <>
            <Table withRowBorders={false} verticalSpacing={2} fz="xs" stickyHeader striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={130}><Text size="xs" c="dimmed">{t("time")}</Text></Table.Th>
                  <Table.Th><Text size="xs" c="dimmed">{t("symbol")}</Text></Table.Th>
                  <Table.Th ta="right"><Text size="xs" c="dimmed">{t("lots")}</Text></Table.Th>
                  <Table.Th><Text size="xs" c="dimmed">{t("direction")}</Text></Table.Th>
                  <Table.Th ta="right"><Text size="xs" c="dimmed">{t("price")}</Text></Table.Th>
                  <Table.Th ta="right"><Text size="xs" c="dimmed">{t("grossEur")}</Text></Table.Th>
                  <Table.Th w={110} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pagedHistory.map((row, i) => {
                  const gross = (row.price ?? 0) * row.filledVolume
                  return (
                    <Table.Tr key={`${String(row.id)}-${i}`}>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {row.filledAt?.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) ?? "–"}
                        </Text>
                      </Table.Td>
                      <Table.Td><Text size="xs" ff="monospace">{ticker(row)}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="xs">{row.filledVolume.toFixed(0)}</Text></Table.Td>
                      <Table.Td>
                        <Text size="xs" fw={700} c={row.side === 1 ? "green" : "red"}>
                          {row.side === 1 ? t("buy") : t("sell")}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="xs" ff="monospace">{row.price != null ? fmtEur(row.price) : "–"}</Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="xs" ff="monospace">{fmtEur(gross)}</Text>
                      </Table.Td>
                      <Table.Td>
                        {row.transactionRefId ? (
                          <Tooltip label={t("viewTradeTooltip")} withArrow position="top">
                            <Button
                              component="a"
                              href={`${REGISTRY_URL}/transaction/${row.transactionRefId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="compact-xs"
                              variant="light"
                              color="teal"
                              rightSection={<TbExternalLink size={12} />}
                            >
                              {t("viewTrade")}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Badge size="xs" color="orange" variant="light">
                            {t("settlementPending")}
                          </Badge>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
            {history.length > PAGE_SIZE && (
              <Group justify="center" py="xs">
                <Pagination size="xs" total={Math.ceil(history.length / PAGE_SIZE)} value={histPage} onChange={setHistPage} />
              </Group>
            )}
            </>
          )}
        </Tabs.Panel>
      </Tabs>

      {tpModalPosition && (
        <SetTPSLModal
          opened
          position={tpModalPosition}
          creditType={tpModalPosition.creditType}
          vintage={tpModalPosition.vintage}
          projectRefId={tpModalPosition.projectRefId}
          lastPrice={marketPrices[`${tpModalPosition.creditType}|${tpModalPosition.vintage}|${tpModalPosition.projectRefId}`] ?? null}
          onClose={() => setTpModalPosition(null)}
          onSuccess={refreshPositions}
        />
      )}
    </Box>
  )
}
